import {QueryCacheKeys} from '../../api/cache';
import {getCategories, getCategoryGroups} from '../../api/categories';
import {getPaymentAccounts} from '../../api/paymentAccounts';
import {getProjects} from '../../api/projects';
import {getTags} from '../../api/tags';
import {formatCurrency, formatTransactionCategory} from '../../formatters';
import {getTransactionsTotal} from '../../helpers/transactions';
import {useSetState} from '../../hooks';
import {List} from '../List';
import MobileFriendlyItemDisplay from '../MobileFriendlyItemDisplay';
import Table from '../Table';
import TransactionProjectPicker from '../forms/TransactionProjectPicker';
import TransactionTagPicker from '../forms/TransactionTagPicker';
import CreateSaleModal from '../sales/CreateSaleModal';
import TagToken from '../tags/TagToken';
import {Box, Icon, Text, Token, type TypeBoxProps} from '@sproutsocial/racine';
import {useQuery} from '@tanstack/react-query';
import {ReactNode, SyntheticEvent, useCallback, useMemo, useState} from 'react';
import {formatFullDate} from 'shared/src/datetime.js';
import {keyBy} from 'shared/src/map.js';
import type {Transaction} from 'shared/src/models';

type TransactionsTableProps = Omit<TypeBoxProps, 'onChange'> & {
	id: string;
	title?: ReactNode;
	transactions: Transaction[];
	allowSelection?: boolean;
	onTransactionsSelected?: (transactionIds: string[]) => void;
	allowEdits?: boolean;
	/**
	 * TODO: Rename to onTransactionsChanged
	 */
	onChange?: () => void;
};

const TransactionsTable = ({
	id: tableId,
	title,
	transactions,
	allowSelection = true,
	onTransactionsSelected,
	allowEdits = true,
	onChange,
	...rest
}: TransactionsTableProps) => {
	const {data: tags} = useQuery({
		queryKey: QueryCacheKeys.tags(),
		queryFn: getTags,
	});

	const {data: categoryGroups} = useQuery({
		queryKey: QueryCacheKeys.categoryGroups(),
		queryFn: getCategoryGroups,
		select: (categoryGroups) => keyBy(categoryGroups, (categoryGroup) => categoryGroup.id),
	});

	const {data: categories} = useQuery({
		queryKey: QueryCacheKeys.categories(),
		queryFn: getCategories,
	});

	const {data: projects} = useQuery({
		queryKey: QueryCacheKeys.projects(),
		queryFn: () => getProjects(),
		select: (projects) => keyBy(projects, (project) => project.id),
	});

	const {data: paymentAccounts} = useQuery({
		queryKey: QueryCacheKeys.paymentAccounts(),
		queryFn: getPaymentAccounts,
		select: (accounts) => keyBy(accounts, (account) => account.id),
	});

	const transactionsMap = useMemo(() => keyBy(transactions ?? [], (t) => t.id), [transactions]);

	const selectedTransactionIds = useSetState<string>();
	const onRowsSelected = useCallback(
		(rowIds: Set<string>) => {
			selectedTransactionIds.clear();

			rowIds.forEach((rowId) => {
				selectedTransactionIds.add(rowId);
			});

			onTransactionsSelected?.(Array.from(selectedTransactionIds));
		},
		[onTransactionsSelected, selectedTransactionIds],
	);

	const selectedTransactions = useMemo(() => {
		return Array.from(selectedTransactionIds)
			.map((transactionId) => transactionsMap.get(transactionId))
			.filter((transaction) => transaction !== undefined);
	}, [selectedTransactionIds, transactionsMap]);

	const allTransactionIds = Array.from(transactionsMap.keys());

	const someTransactionsSelected = useMemo<boolean>(() => {
		return allTransactionIds.some((id) => selectedTransactionIds.has(id));
	}, [selectedTransactionIds, allTransactionIds]);

	const totals = useMemo(() => {
		return {
			amount: getTransactionsTotal(transactions),
			selectedAmount: getTransactionsTotal(selectedTransactions),
		};
	}, [transactions, selectedTransactions]);

	const sortTransactions = useCallback(
		(a: Transaction, b: Transaction, id: string): number => {
			switch (id) {
				case 'date':
					return a.date.toMillis() - b.date.toMillis();
				case 'amount':
					return a.amount - b.amount;
				case 'payee':
					return (a.payee ?? '').toLowerCase().localeCompare((b.payee ?? '').toLowerCase());
				default:
					return 0;
			}
		},
		[categories],
	);

	const [saleCreationTransactionId, setSaleCreationTransactionId] = useState<string | null>(null);
	const onCreateSale = useCallback((event: SyntheticEvent<HTMLElement>) => {
		setSaleCreationTransactionId(event.currentTarget.dataset['transactionId']!);
	}, []);
	const onCreateSaleModalClosed = useCallback(
		(saleCreated?: boolean) => {
			setSaleCreationTransactionId(null);

			if (saleCreated) {
				onChange?.();
			}
		},
		[onChange],
	);

	if (!transactions || !tags || !categoryGroups || !categories) {
		return null;
	}

	return (
		<>
			<MobileFriendlyItemDisplay
				{...rest}
				list={
					<List id={tableId}>
						<List.Item
							data={
								<Text
									fontSize={300}
									fontWeight='semibold'
									children={formatCurrency(totals.amount)}
								/>
							}
						/>
						{transactions.map((transaction) => (
							<List.Item
								key={transaction.id}
								title={transaction.payee ?? transaction.memo}
								description={formatTransactionCategory(categories.get(transaction.category)!)}
								data={formatCurrency(transaction.amount)}
							/>
						))}
					</List>
				}
				table={
					<Table
						id={tableId}
						title={title}
						head={[
							{id: 'account-type', content: ''},
							{id: 'date', content: 'Date', isSortable: true},
							{id: 'payee', content: 'Payee', isSortable: true},
							{id: 'category', content: 'Category'},
							{id: 'memo', content: 'Memo'},
							{id: 'amount', content: 'Amount', isSortable: true},
							{
								id: 'sale',
								content: 'Sale',
							},
							{
								id: 'tags',
								content: allowEdits && someTransactionsSelected && (
									<TransactionTagPicker transactions={selectedTransactions} onChange={onChange} />
								),
							},
							{
								id: 'projects',
								content: allowEdits && someTransactionsSelected && (
									<TransactionProjectPicker
										transactions={selectedTransactions}
										onChange={onChange}
									/>
								),
							},
						]}
						fixedRow={{
							id: 'totals',
							cells: [
								null,
								null,
								null,
								null,
								null,
								<Text fontWeight='semibold'>
									{formatCurrency(totals.amount)}
									{selectedTransactions.length > 0 && (
										<>
											<br />({formatCurrency(totals.selectedAmount)})
										</>
									)}
								</Text>,
								null,
								null,
							],
						}}
						items={transactions}
						generateRow={(transaction) => {
							if (paymentAccounts?.get(transaction.paymentAccount)?.type !== 'CREDIT') {
								debugger;
							}
							return {
								id: transaction.id,
								cells: [
									<Icon
										name={
											paymentAccounts?.get(transaction.paymentAccount)?.type === 'BANK'
												? 'building-columns-solid-full'
												: paymentAccounts?.get(transaction.paymentAccount)?.type === 'CASH'
													? 'dollar-solid'
													: 'credit-card-solid'
										}
									/>,
									<Text>{formatFullDate(transaction.date)}</Text>,
									transaction.payee,
									formatTransactionCategory(categories.get(transaction.category)!),
									transaction.memo,
									<Text color={transaction.amount > 0 ? 'green.700' : 'text.body'}>
										{formatCurrency(transaction.amount)}
									</Text>,
									<>
										{transaction.sale && <Icon name='cart-solid' />}
										{!transaction.sale && transaction.amount > 0 && (
											<Icon
												name='cart-plus-outline'
												onClick={onCreateSale}
												cursor='pointer'
												data-transaction-id={transaction.id}
											/>
										)}
									</>,
									<TransactionTagPicker
										transactions={[transaction]}
										disabled={!allowEdits}
										onChange={onChange}
									/>,
									<TransactionProjectPicker
										transactions={[transaction]}
										disabled={!allowEdits}
										onChange={onChange}
									/>,
								],
								drawerContents: (transaction.tags.length > 0 ||
									transaction.projects.length > 0) && (
									<Box>
										{transaction.tags.length > 0 && (
											<>
												<Text as='div' fontSize='200' fontWeight='semibold' mb='space.300'>
													Tags:
												</Text>
												<Box>
													{transaction.tags.map((tagId) => (
														<TagToken
															key={tagId}
															id={tagId}
															closeable={false}
															includeParents
															mr='space.300'
															mb='space.300'
														/>
													))}
												</Box>
											</>
										)}
										{transaction.projects.length > 0 && (
											<>
												<Text as='div' fontSize='200' fontWeight='semibold' mb='space.300'>
													Projects:
												</Text>
												<Box>
													{transaction.projects.map((projectId) => (
														<Token key={projectId} mr='space.300' mb='space.300'>
															<Box display='flex' alignItems='center'>
																<Icon name='screwdriver-hammer-solid' mr='space.300' />
																{projects?.get(projectId)?.name}
															</Box>
														</Token>
													))}
												</Box>
											</>
										)}
									</Box>
								),
							};
						}}
						allowSelection={allowSelection}
						onRowsSelected={onRowsSelected}
						sort={sortTransactions}
					/>
				}
			/>

			{saleCreationTransactionId !== null && (
				<CreateSaleModal
					transactionId={saleCreationTransactionId}
					onClose={onCreateSaleModalClosed}
				/>
			)}
		</>
	);
};

export default TransactionsTable;
